import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  ActivityIndicator,
  Image,
  type LayoutChangeEvent,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import Svg, { Path } from 'react-native-svg';
import { GestureDetector, GestureHandlerRootView } from 'react-native-gesture-handler';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useLocale } from '../i18n/LocaleContext';
import {
  AVATAR_SHAPE_PATHS,
  DEFAULT_AVATAR_SHAPE,
  normalizeAvatarShapeId,
} from '../avatar/shapeCatalog';
import { getOrDownloadAvatarUri, peekCachedAvatarUri } from '../util/mediaCache';
import {
  clampPhotoZoomRect,
  IDENTITY_PHOTO_ZOOM,
  photoFrameSideForBox,
  toggleDoubleTapZoom,
  type PhotoZoomViewport,
} from '../util/photoZoomViewport';
import { usePhotoZoomGestures } from '../util/usePhotoZoomGestures';

type Props = {
  visible: boolean;
  userId: string;
  displayName: string;
  /** Ревизия аватара: тот же ключ кэша, по которому снимок уже скачал бейдж. */
  revision: number;
  /** Форма оправы — та же, что у бейджа в списке. Пустая означает форму по умолчанию. */
  shapeId?: string | null;
  onClose: () => void;
};

/** Толщина линии оправы на экране. В единицы viewBox пересчитывается от стороны медальона. */
const FRAME_STROKE_PX = 3.5;

/**
 * Паспарту — поле вокруг снимка, и оно глухое: за оправой не видно ничего. Полупрозрачное
 * пробовали (заказ владельца 2026-09-05 — «чтобы всё, что за рамкой, не было видно»): даже
 * притушенный до тени снимок за оправой читался второй картинкой и спорил с той, что в
 * рамке. Смотрят одно лицо, а не два.
 */
const FRAME_MAT_FILL = '#000';
/** Тень под линией: отделяет светлую оправу от светлого снимка, не утолщая саму линию. */
const FRAME_HALO_STROKE = 'rgba(0, 0, 0, 0.38)';
/**
 * Линия оправы. Цвет не из темы (`avatarChrome.outline`), в отличие от бейджа: тема даёт
 * контур под цвет своего фона, а фон просмотра всегда почти чёрный, и тёмная линия на нём
 * просто исчезла бы.
 */
const FRAME_LINE_STROKE = 'rgba(248, 250, 252, 0.92)';

/**
 * Фотография человека крупно — то, что в мессенджерах открывается касанием аватара.
 *
 * <p><b>Снимок в оправе, а не вместо неё.</b> Раньше касание разворачивало голый квадрат:
 * форма и контур, которые человек сам выбрал себе аватаром, до крупного просмотра не
 * доживали, и вместо «того же лица поближе» открывалась другая картинка. Теперь рамка —
 * это окно: она стоит на месте, а снимок ходит и приближается внутри неё. Поэтому границы
 * сдвига считаются от стороны рамки, а не от экрана ({@link photoFrameSideForBox}): при
 * любом приближении оправа остаётся залитой кадром, без пустых щелей по краю.
 *
 * <p>То, что вышло за оправу, не пропадает, а уходит под паспарту (см. {@link FRAME_MAT_FILL}):
 * иначе приближённый снимок выглядел бы обрезанным, а не подвинутым.
 *
 * <p><b>Почему слой, а не {@code <Modal>}.</b> Открывают его всегда из уже показанной
 * карточки профиля, а вторая одновременная презентация RN-модалки подвешивает экран на iOS
 * и уезжает под первую на Android — те же грабли описаны в `UserAvatarPreviewModal`.
 * Поэтому здесь обычный слой поверх содержимого карточки.
 *
 * <p>Снимок берётся из того же кэша, что и бейдж (`getOrDownloadAvatarUri`), — значит, у
 * открытой карточки он уже скачан и появляется без задержки и без второго запроса к серверу.
 *
 * <p>Кому показывать, решает не этот компонент: право смотреть фото проверяет карточка
 * (см. `UserAvatarPreviewTarget.canViewPhoto`).
 */
export function UserPhotoViewer({
  visible,
  userId,
  displayName,
  revision,
  shapeId,
  onClose,
}: Props): React.ReactElement | null {
  const { t } = useLocale();
  const insets = useSafeAreaInsets();

  const [uri, setUri] = useState<string | null>(() =>
    revision > 0 ? peekCachedAvatarUri(userId, revision) : null,
  );
  const [failed, setFailed] = useState(false);
  const [box, setBox] = useState({ width: 0, height: 0 });
  const [viewport, setViewport] = useState<PhotoZoomViewport>(IDENTITY_PHOTO_ZOOM);

  /**
   * Состояние для колбэков жеста — через ref: иначе объекты `Gesture.*` пересобирались бы
   * на каждый кадр перетаскивания, ровно тогда, когда жест активен (см. `AvatarPhotoEditor`).
   */
  const viewportRef = useRef<PhotoZoomViewport>(IDENTITY_PHOTO_ZOOM);
  /** Сторона рамки — она же окно просмотра, от которого считаются границы сдвига. */
  const frameSideRef = useRef(0);

  // Открыли (или сменился снимок): горячий кеш — сразу, в том же рендере; иначе грузим ниже.
  const photoKey = visible ? `${userId}:${revision}` : null;
  const [shownKey, setShownKey] = useState<string | null>(null);
  if (shownKey !== photoKey) {
    setShownKey(photoKey);
    if (photoKey != null) {
      setFailed(false);
      const hot = revision > 0 ? peekCachedAvatarUri(userId, revision) : null;
      if (hot) setUri(hot);
    }
  }

  useEffect(() => {
    if (!visible) return;
    if (revision > 0 && peekCachedAvatarUri(userId, revision)) return;
    let cancelled = false;
    void getOrDownloadAvatarUri(userId, revision)
      .then((u) => {
        if (!cancelled) setUri(u);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [revision, userId, visible]);

  // Каждое открытие начинается со снимка целиком: приближение прошлого просмотра —
  // не то, что человек ждёт, открывая фото заново.
  const [prevVisible, setPrevVisible] = useState(visible);
  if (prevVisible !== visible) {
    setPrevVisible(visible);
    if (!visible) setViewport(IDENTITY_PHOTO_ZOOM);
  }
  useEffect(() => {
    if (!visible) viewportRef.current = IDENTITY_PHOTO_ZOOM;
  }, [visible]);

  const applyViewport = useCallback((next: PhotoZoomViewport) => {
    const side = frameSideRef.current;
    // Окно здесь — сама рамка: снимок обязан закрывать её целиком в любом положении.
    const clamped = clampPhotoZoomRect(next, side, side, side, side);
    viewportRef.current = clamped;
    setViewport(clamped);
  }, []);

  const gesture = usePhotoZoomGestures({
    viewportRef,
    apply: applyViewport,
    onDoubleTap: (current) => toggleDoubleTapZoom(current),
    onSingleTap: onClose,
  });

  const onStageLayout = useCallback((e: LayoutChangeEvent) => {
    const { width, height } = e.nativeEvent.layout;
    setBox({ width, height });
  }, []);

  const shapePath = AVATAR_SHAPE_PATHS[normalizeAvatarShapeId(shapeId ?? DEFAULT_AVATAR_SHAPE)];

  // Сверху имя с крестиком, снизу подсказка: по вертикали медальону оставляем их полосу,
  // иначе на низком окне (планшет лёжа, окно браузера) он уедет под них.
  const chromeInset = Math.max(insets.top, insets.bottom) + 52;
  const side = photoFrameSideForBox(box.width, box.height, undefined, chromeInset);
  useLayoutEffect(() => {
    frameSideRef.current = side;
  }, [side]);

  /** Паспарту в координатах формы (0–100): экран, из которого вырезана оправа. */
  const mat = useMemo(() => {
    if (side <= 0 || box.width <= 0 || box.height <= 0) return null;
    const unit = side / 100; // пикселей в одной единице viewBox
    const x0 = -(box.width - side) / 2 / unit;
    const y0 = -(box.height - side) / 2 / unit;
    const w = box.width / unit;
    const h = box.height / unit;
    const n = (v: number): string => v.toFixed(2);
    return {
      viewBox: `${n(x0)} ${n(y0)} ${n(w)} ${n(h)}`,
      // Одним контуром с evenodd: внешний прямоугольник заливается, форма внутри вычитается.
      cutout: `M ${n(x0)} ${n(y0)} h ${n(w)} v ${n(h)} h ${n(-w)} Z ${shapePath}`,
      stroke: FRAME_STROKE_PX / unit,
    };
  }, [box.height, box.width, shapePath, side]);

  if (!visible) return null;

  return (
    // GestureHandlerRootView свой: карточка нередко живёт внутри RN-модалки, а туда
    // корень из App.tsx не достаёт — жесты в модалке без него не доходят на Android.
    <GestureHandlerRootView style={[StyleSheet.absoluteFillObject, styles.layer]}>
      <View style={styles.stage} onLayout={onStageLayout}>
        {uri && mat ? (
          <GestureDetector gesture={gesture}>
            <View style={StyleSheet.absoluteFill}>
              <View style={styles.center} pointerEvents="none">
                <Image
                  source={{ uri }}
                  style={{
                    width: side,
                    height: side,
                    transform: [
                      { translateX: viewport.tx },
                      { translateY: viewport.ty },
                      { scale: viewport.scale },
                    ],
                  }}
                  // Как у бейджа (`preserveAspectRatio="slice"`): оправа должна быть залита
                  // кадром, а не полями. Аватар и так приходит квадратом 512 px.
                  resizeMode="cover"
                  accessibilityLabel={displayName}
                />
              </View>
              {/* Оправа лежит поверх снимка и жестов не перехватывает: она стекло, а не
                  кнопка. `pointerEvents` стоит на обёртке, а не на <Svg>: так его читает
                  RN сам, не полагаясь на то, донесёт ли его до нативного вида библиотека. */}
              <View style={StyleSheet.absoluteFill} pointerEvents="none">
                <Svg width={box.width} height={box.height} viewBox={mat.viewBox}>
                  <Path d={mat.cutout} fill={FRAME_MAT_FILL} fillRule="evenodd" />
                  <Path
                    d={shapePath}
                    fill="none"
                    stroke={FRAME_HALO_STROKE}
                    strokeWidth={mat.stroke * 2}
                    strokeLinejoin="round"
                  />
                  <Path
                    d={shapePath}
                    fill="none"
                    stroke={FRAME_LINE_STROKE}
                    strokeWidth={mat.stroke}
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                </Svg>
              </View>
            </View>
          </GestureDetector>
        ) : (
          <View style={styles.center}>
            {failed ? (
              <Text style={styles.failedText}>{t('userCardPhotoLoadFailed')}</Text>
            ) : (
              <ActivityIndicator color="#fff" />
            )}
          </View>
        )}
      </View>

      {/* Шапка рисуется после сцены — значит, поверх неё, и её кнопка ловит касание первой. */}
      <View style={[styles.topBar, { paddingTop: insets.top + 8 }]} pointerEvents="box-none">
        <Text style={styles.name} numberOfLines={1}>
          {displayName}
        </Text>
        <Pressable
          onPress={onClose}
          hitSlop={12}
          style={styles.closeBtn}
          accessibilityRole="button"
          accessibilityLabel={t('close')}>
          <Text style={styles.closeIcon}>✕</Text>
        </Pressable>
      </View>

      <Text style={[styles.hint, { paddingBottom: insets.bottom + 12 }]} pointerEvents="none">
        {t('userCardPhotoHint')}
      </Text>
    </GestureHandlerRootView>
  );
}

const styles = StyleSheet.create({
  // Поверх карточки профиля, из которой снимок и открыли.
  layer: { zIndex: 40, backgroundColor: 'rgba(0, 0, 0, 0.96)' },
  stage: { flex: 1 },
  center: { ...StyleSheet.absoluteFillObject, alignItems: 'center', justifyContent: 'center' },
  topBar: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingHorizontal: 16,
    paddingBottom: 8,
  },
  name: { flex: 1, color: '#fff', fontSize: 16, fontWeight: '700' },
  closeBtn: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.14)',
  },
  closeIcon: { color: '#fff', fontSize: 17, fontWeight: '700', lineHeight: 20 },
  hint: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    textAlign: 'center',
    color: 'rgba(255, 255, 255, 0.6)',
    fontSize: 12,
    paddingHorizontal: 24,
  },
  failedText: { color: 'rgba(255, 255, 255, 0.8)', fontSize: 14, textAlign: 'center' },
});
